import { useState, useEffect } from "react";
import { Save, ArrowLeft, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import type { OwnershipType } from "@/types/domain";

interface LocationSidebarEditProps {
  locationId: string;
  onBack: () => void;
  isPinEditActive?: boolean;
  hasPinMoved?: boolean;
  isSavingPin?: boolean;
  onEnterPinEdit?: () => void;
  onSavePinEdit?: () => void;
  onCancelPinEdit?: () => void;
}

export function LocationSidebarEdit({
  locationId,
  onBack,
  isPinEditActive,
  hasPinMoved,
  isSavingPin,
  onEnterPinEdit,
  onSavePinEdit,
  onCancelPinEdit,
}: LocationSidebarEditProps) {
  const { data: location } = trpc.locations.getById.useQuery(
    { id: locationId },
    { enabled: !!locationId },
  );

  const utils = trpc.useUtils();

  const updateMutation = trpc.locations.updateOverrides.useMutation({
    onSuccess: () => {
      utils.locations.getById.invalidate({ id: locationId });
      utils.locations.getMapData.invalidate();
    },
  });

  const [userNotes, setUserNotes] = useState("");
  const [ownershipOverride, setOwnershipOverride] =
    useState<OwnershipType>("unknown");

  // Initialize form state from location data
  useEffect(() => {
    if (location) {
      setUserNotes(location.user_notes ?? "");
      setOwnershipOverride(
        (location.ownership as OwnershipType) ?? "unknown",
      );
    }
  }, [location]);

  const handleSave = () => {
    updateMutation.mutate({
      locationId,
      userNotes: userNotes || null,
      ownership: ownershipOverride,
    });
  };

  return (
    <div className="space-y-4 p-4 overflow-y-auto h-full">
      {/* Pin location adjustment */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Pin Location</label>
        {isPinEditActive ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
            <p className="text-xs text-amber-800">
              {hasPinMoved ? "Pin moved — save or cancel" : "Drag the pin on the map to reposition"}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="flex-1 h-8 text-xs"
                onClick={onCancelPinEdit}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="flex-1 h-8 text-xs"
                disabled={!hasPinMoved || isSavingPin}
                onClick={onSavePinEdit}
              >
                {isSavingPin ? "Saving..." : "Save Position"}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-full"
            onClick={onEnterPinEdit}
          >
            <MapPin className="mr-2 size-4" />
            Adjust Pin Location
          </Button>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Notes</label>
        <Textarea
          placeholder="Add your notes about this location..."
          value={userNotes}
          onChange={(e) => setUserNotes(e.target.value)}
          rows={3}
        />
      </div>

      {/* Ownership override */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Ownership Override</label>
        <Select
          value={ownershipOverride}
          onValueChange={(v) => setOwnershipOverride(v as OwnershipType)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="public">Public</SelectItem>
            <SelectItem value="private">Private</SelectItem>
            <SelectItem value="institutional">Institutional</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Save button */}
      <Button
        onClick={handleSave}
        disabled={updateMutation.isPending}
        className="w-full"
      >
        <Save className="mr-2 size-4" />
        {updateMutation.isPending ? "Saving..." : "Save Changes"}
      </Button>

      {updateMutation.isSuccess && (
        <p className="text-sm text-green-600">Changes saved.</p>
      )}
      {updateMutation.isError && (
        <p className="text-sm text-destructive">
          Failed to save: {updateMutation.error.message}
        </p>
      )}

      {/* Back button */}
      <Button variant="ghost" className="w-full" onClick={onBack}>
        <ArrowLeft className="mr-2 size-4" />
        Back to Details
      </Button>
    </div>
  );
}
